<template>
  <v-card class="chart-revoked">
    <v-progress-linear
      style="position: absolute"
      indeterminate
      color="cyan"
      v-if="loading"
    ></v-progress-linear>
    <div>
      <reports-heading
        class="chart-heading"
        :total="getRevokedCertificates.total"
        title="Revoked certificates"
        tooltip="Statistics for revoked certificates per selected CA and time period."
        @callSetReports="callSetReports"
      />
    </div>
    <div class="chart-div">
      <div class="pt-8 pb-4">
        <v-row no-gutters>
          <v-col cols="1"></v-col>
          <v-col cols="10">
            <apexchart
              type="bar"
              height="265"
              :options="chartOptions"
              :series="series"
            ></apexchart>
          </v-col>
          <v-col cols="1"></v-col>
        </v-row>
      </div>
      <div
        class="text-right"
        elevation="0"
        v-if="chartFilters && getRevokedCertificates.total"
      >
        <v-btn color="#7298B7" text dense small @click="viewList">View list</v-btn>
      </div>
      <div v-else style="height: 1.75rem"></div>
    </div>
  </v-card>
</template>

<script>
import ReportsHeading from "@/components/reports/reports-items/reports-heading.vue";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import * as constants from "@/config/constants";

export default {
  name: "reports-revoked-certificates",
  components: { ReportsHeading },
  computed: {
    ...mapGetters(["getRevokedCertificates"]),
  },
  data() {
    return {
      customTickAmount: "", // Variable to hold the tick amount
      loading: false,
      chartFilters: null,
      series: [
        {
          name: "Revoked Certs",
          data: [],
        },
      ],
      chartOptions: {
        chart: {
          type: "bar",
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "30%",
            endingShape: "rounded",
          },
        },
        responsive: [
          {
            breakpoint: this.$vuetify.breakpoint.mdAndDown,
            options: {
              plotOptions: {
                bar: {
                  horizontal: false,
                  columnWidth: "70%",
                  endingShape: "rounded",
                },
              },
            },
          },
        ],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          show: true,
          tickPlacement: "between",
          categories: [],
          labels: {},
        },
        yaxis: {
          forceNiceScale: false,
          tickAmount: this.customTickAmount, // Change this number to fit your needs
          labels: {
            formatter: function (val) {
              return val === Infinity || val === 0 ? "" : Math.round(val); // Display whole numbers
            },
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          x: {},
        },
        colors: ["#006CA1"],
      },
    };
  },
  methods: {
    ...mapActions([
      "updateRevokedCertificates",
      "updateCertificates",
      "updateCertificateSearchParams",
    ]),
    async viewList() {
      const searchParams = {
        revocationReason: constants.REVOCATION_REASONS.map((reason) => reason.id).join(
          ","
        ),
      };
      if (this.chartFilters.params.breakpoints) {
        let breakpoints = this.chartFilters.params.breakpoints.split(",");
        searchParams.revocationTimeFrom = moment
          .utc(breakpoints[0], "YYYYMMDDhhmmssZ")
          .toISOString();
        searchParams.revocationTimeTo = moment
          .utc(breakpoints[breakpoints.length - 1], "YYYYMMDDhhmmssZ")
          .toISOString();
      }
      if (this.chartFilters.params.issuer) {
        searchParams.issuer = this.chartFilters.params.issuer;
      }
      await this.updateCertificateSearchParams(searchParams);
      await this.$router.push({ name: "Certificates" });
    },
    callSetReports(payload) {
      this.loading = true;
      this.updateRevokedCertificates(payload.params).then(() => {
        this.injectNewDataToChart();
        this.setXaxisChartOption({
          firstPoint: payload.firstDate,
          timeRangeType: payload.timeRangeType,
        });
        this.loading = false;
        this.chartFilters = payload;

        // Sets the tooltip values
        this.chartOptions.tooltip.x = {
          formatter: function (seriesName) {
            if (payload.timeRangeType === "week") {
              return seriesName;
            } else {
              // Gets the month number from the date
              const monthNumber = moment.utc(payload.firstDate).format("M");
              return `${seriesName}/${monthNumber}`;
            }
          },
        };

        // If type month and screens are medium or below
        if (payload.timeRangeType === "month" && this.$vuetify.breakpoint.mdAndDown) {
          this.chartOptions.xaxis.labels = {
            formatter: function (value, timestamp, ots) {
              // If value is not the power of five
              // E.g 1,2,3,4,6,7
              if (value % 5) {
                return "";
              }
              // If value is the power of five
              // E.g 5,10,15,20
              else {
                return value;
              }
            },
          };
        }
      });
    },
    injectNewDataToChart() {
      let tempSeries = [...this.series];
      this.series = [];
      tempSeries[0].data = [];
      this.getRevokedCertificates.certs.forEach((revokeCert) => {
        tempSeries[0].data.push(revokeCert);
      });
      this.series = tempSeries;
    },
    setXaxisChartOption({ firstPoint, timeRangeType }) {
      this.chartOptions.xaxis.categories = [];
      const temp = { ...this.chartOptions };
      this.chartOptions = {};
      this.chartOptions = { ...temp };

      if (timeRangeType === "week") {
        for (let i = 0; i < 7; i++) {
          this.chartOptions.xaxis.categories.push(
            moment.utc(firstPoint).add(i, "days").format("DD[/]MM")
          );
        }
      } else if (timeRangeType === "month") {
        let days = moment.utc(firstPoint).daysInMonth();
        for (let i = 1; i <= days; i++) {
          this.chartOptions.xaxis.categories.push(i);
        }
      }
    },
  },
  mounted() {
    this.injectNewDataToChart();
    this.setXaxisChartOption({
      firstPoint: moment.utc().startOf("isoWeek"),
      timeRangeType: "week",
    });
  },
};
</script>
